import React from 'react';
import {
  Box,
  Divider,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  alpha,
} from '@mui/material';
import { UserAvatar } from '@components/common/UserAvatar.tsx';
import { getSidebarColors } from '@/styles/roleThemes';

// Правий сайдбар карток продакт-менеджера (клієнт /pm/client/:id, експерт /pm/expert/:id), 2026-10-10.
// Спрощений клон ClientProfileSidebar експерта: аватар, ім'я і меню розділів. Без перемикача, груп, бейджів і
// перевірки доступів. Розділи передає сторінка.
// Шапка й акценти - кольором ролі (клієнт зелений, експерт синій, з roleThemes) з підписом «Клієнт»/«Експерт», щоб
// продакт одразу бачив, у чиїй картці він зараз.

export interface PmProfileTab {
  path: string;
  label: string;
  icon: React.ReactElement;
}

export type PmProfileKind = 'client' | 'expert';

const KIND_LABELS: Record<PmProfileKind, string> = {
  client: 'Клієнт',
  expert: 'Експерт',
};

interface PmProfileSidebarProps {
  kind: PmProfileKind;
  tabs: PmProfileTab[];
  activeTab: string;
  onTabChange: (path: string) => void;
  name: string;
  avatar?: string | null;
  isMobileOpen?: boolean;
  onClose?: () => void;
  // Внутрішній id користувача (той самий user_id, що в Amplitude) — показується під ім'ям.
  userId?: number;
  // Додатковий блок під меню (напр. посилання на Amplitude).
  footer?: React.ReactNode;
}

export const PmProfileSidebar: React.FC<PmProfileSidebarProps> = ({
  kind,
  tabs,
  activeTab,
  onTabChange,
  name,
  avatar,
  isMobileOpen = false,
  onClose,
  userId,
  footer,
}) => {
  const { background: roleColor, color: roleTextColor } = getSidebarColors(kind);

  const content = (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', bgcolor: 'background.paper', overflowY: 'auto' }}>
      <Box
        sx={{
          px: 2,
          pt: 1.5,
          pb: 1.5,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1,
          bgcolor: roleColor,
          color: roleTextColor,
        }}
      >
        <Typography variant="caption" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: 1, opacity: 0.9 }}>
          {KIND_LABELS[kind]}
        </Typography>
        <UserAvatar path={avatar ?? undefined} name={name} sx={{ width: 40, height: 40 }} />
        <Typography variant="body2" fontWeight={700} align="center" noWrap sx={{ fontSize: '0.85rem', maxWidth: '100%' }}>
          {name}
        </Typography>
        {userId !== undefined && (
          <Typography variant="caption" sx={{ opacity: 0.9 }}>
            ID {userId}
          </Typography>
        )}
      </Box>

      <Divider />

      <List disablePadding sx={{ py: 0.5 }}>
        {tabs.map((tab) => {
          const isActive = activeTab === tab.path;
          return (
            <ListItemButton
              key={tab.path}
              selected={isActive}
              onClick={() => {
                onTabChange(tab.path);
                onClose?.();
              }}
              sx={{
                minHeight: 40,
                px: 2,
                borderRadius: 0,
                '&.Mui-selected, &.Mui-selected:hover': {
                  bgcolor: alpha(roleColor, 0.1),
                  color: roleColor,
                  '& .MuiListItemIcon-root': { color: roleColor },
                  borderRight: '3px solid',
                  borderColor: roleColor,
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 0, mr: 1.5, color: isActive ? roleColor : 'text.secondary' }}>
                {React.cloneElement(tab.icon, { fontSize: 'small' })}
              </ListItemIcon>
              <ListItemText
                primary={tab.label}
                primaryTypographyProps={{ fontSize: '0.85rem', noWrap: true, fontWeight: isActive ? 600 : 400 }}
              />
            </ListItemButton>
          );
        })}
      </List>

      {footer}
    </Box>
  );

  return (
    <>
      <Drawer
        anchor="right"
        open={isMobileOpen}
        onClose={onClose}
        sx={{
          display: { xs: 'block', md: 'none' },
          zIndex: (theme) => theme.zIndex.drawer + 2,
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 200, borderLeft: '3px solid', borderColor: roleColor },
        }}
      >
        {content}
      </Drawer>

      <Box
        sx={{
          width: 200,
          position: 'fixed',
          top: '52px',
          right: 0,
          height: 'calc(100vh - 52px)',
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          bgcolor: 'background.paper',
          borderLeft: '3px solid',
          borderColor: roleColor,
          overflowY: 'auto',
          zIndex: (theme) => theme.zIndex.appBar - 1,
        }}
      >
        {content}
      </Box>
    </>
  );
};
